import { Bookmark, VideoBookmark } from './Bookmark';
import { BlockTimeSetting, BlockType } from './Study';
import { SubscriptionPlan } from './Subscription';
import { School } from './School';
import { FeedbackTrigger } from './Feedback';
import { WIDGET_KEYS } from './Widgets';
import { FlashcardKeybinds } from './Keybind';
import { NotificationPreferences } from './NotificationPreferences';
import { QbankStartConfig } from './QbankStartConfig';
import { MessageState } from './messageState';
import type { ResolvedInstance } from './Instance';

export type ResponseOption = 'two' | 'four';

export enum UserRole {
  default = 'default',
  free_access = 'free_access',
  admin = 'admin',
  qa = 'qa',
  qa_qbank = 'qa_qbank',
  qa_flashcards = 'qa_flashcards',
}

export enum ExamRole {
  READ = 'READ',
  WRITE = 'WRITE',
  ADMIN = 'ADMIN',
  OWNER = 'OWNER',
}

export type DailyNewMode = 'RECOMMENDED' | 'FIXED';
export type DailyDueMode = 'ALL' | 'FIXED';

export type ThemeMode = 'system' | 'light' | 'dark';

/**
 * How long the pointer rests on a dashed-underline term before its definition
 * pop-up opens (web hover only). Mirrors the `ExplainerHoverDelay` enum.
 */
export type ExplainerHoverDelay = 'default' | 'long';
type MappingRelevance = 'FOCUSED' | 'BALANCED' | 'BROAD';
type ScreensaverOption =
  | 'none'
  | 'thirtySeconds'
  | 'oneMinute'
  | 'twoMinutes'
  | 'fiveMinutes'
  | 'tenMinutes';

interface SessionFeedbackTracking {
  lastFeedback: Date;
  types: {
    overall?: Date;
    qbank?: Date;
    videos?: Date;
    flashcards?: Date;
  };
}

export interface UserExam {
  id: string;
  name: string;
  examDate?: Date | null | string;
  primary: boolean;
  selectedFlashcardCount: number;
  selectedVignetteCount: number;
  dailyFlashcardsNewMode?: DailyNewMode;
  dailyFlashcardsNewFixedCount?: number;
  dailyFlashcardsDueMode?: DailyDueMode;
  dailyFlashcardsDueFixedCap?: number;
  dailyQbankNewMode?: DailyNewMode;
  dailyQbankNewFixedCount?: number;
  dailyQbankDueMode?: DailyDueMode;
  dailyQbankDueFixedCap?: number;
  dailyVideosNewMode?: DailyNewMode;
  dailyVideosNewFixedCount?: number;
  isDefaultDate: boolean;
  isUsingFlashcards: boolean;
  isUsingQbank: boolean;
  isUsingVideos: boolean;
  hasCompletedRealExam: boolean;
  realExamScore: number | null;
  ignoredExamDate: boolean;
  customExamSortOrder?: number | null;
  type?:
    | 'CUSTOM'
    | 'NBME'
    | 'COMLEX'
    | 'RESIDENCY'
    | 'FREE_STUDY'
    | 'PRE_CLINICAL'
    | 'NCLEX'
    /** A partner course (Courses V1): an exam that is a prescriptive path. */
    | 'COURSE';
  schoolId?: string | null;
  school: School | null;
  verified: boolean;
  lectureMode: 'AUTOPILOT' | 'MANUAL';
  role?: ExamRole;
  sharedWithSchool?: boolean;
  /** Institution-provisioned exam: available through a school contract, not
   *  a classmate's share, so there is nothing to upvote. */
  requiresInstitutionalAccess?: boolean;
  isActiveCustomExam?: boolean;
  isOwner?: boolean;
  ownerEmail?: string;
  ownerName?: string;
  createdAt?: Date;
  updatedAt?: Date;
  videos?: {
    total: number;
    watched: number;
    new: number;
  };
  flashcards?: {
    total: number;
    new: number;
    due: number;
    completed: number;
  };
  vignettes?: {
    total: number;
    correct: number;
    studied: number;
    new: number;
  };
  lectureCount?: number;
  /** Users enrolled on this exam (any role). Absent on responses that
   *  predate the field; only >1 means the exam is not deletable. */
  memberCount?: number;
}

export enum ExplanationType {
  DEFAULT = 'DEFAULT',
  SHORT = 'SHORT',
}

export interface User {
  id: string;
  name?: string | null;
  email: string | null;
  /** Better Auth's flag: the primary email's verification link was used. */
  emailVerified?: boolean;
  createdAt: Date;
  oauth?: boolean | null;
  oauthStrategy?: string | null;
  role: UserRole;
  // Optional expiry for the free_access role (ISO-8601). null/undefined =
  // indefinite. When past, the user keeps the role but reverts to non-pay
  // access (gated backend-side). See ORA-210.
  freeAccessEndsAt?: string | null;
  applyDailySessionSizeToAllStandardExams?: boolean;
  timezone: string;
  isOnboarded: boolean;
  isActivated: boolean;
  activatedAt?: Date | null;
  activationReason?: string | null;
  isAmbassadorOnboarded: boolean;
  onboardingTips: { [key: string]: boolean };
  messageStates?: MessageState[];
  theme: ThemeMode;
  highContrast: boolean;
  demoMode: boolean;
  screensaverPreference: ScreensaverOption;
  responseOptions: ResponseOption;
  showIntervals: boolean;
  desiredRetention?: number;
  showAISidebar: boolean;
  hapticsEnabled?: boolean;
  /** Keep one companion chat for each QBank block by default (ORA-1285). */
  chatBlockThreadDefault?: boolean;
  explainerHoverDelay?: ExplainerHoverDelay;
  allowedBetaUser: boolean;
  exams: UserExam[];
  preferredBlockType: BlockType;
  preferredBlockTimeSetting: BlockTimeSetting;
  // Computed server-side: true when the user is an enrolled cohort student, in
  // which case the self-serve time-accommodation control is locked.
  isTimeAccommodationLocked?: boolean;
  qbankTheme?: 'standard' | 'ora';
  isAnon?: boolean;
  bookmarks: Bookmark[];
  videoBookmarks: VideoBookmark[];
  hasIOSApp: boolean;
  hasAndroidApp: boolean;
  flashcardTextSize: string;
  vignetteTextSize: string;
  vignetteMobileTextSize: string;
  studyReminders: string;
  subscribedToEmails: boolean;
  emailPreferences?: NotificationPreferences;
  isApprovedReferrer: boolean;
  shareGameScores: boolean;
  referralId?: string;
  buyIntent: SubscriptionPlan | null;
  lastFeedbackTrigger?: FeedbackTrigger | null;
  ambassador: boolean;
  ambassadorStartDate: Date | null;
  ambassadorEndDate: Date | null;
  ambassadorPaid: boolean;
  ambassadorPayment: string | null;
  ambassadorAdvisoryBoard: string;
  ambassadorOnWebsite: string;
  ambassadorOnSlack: string;
  ambassadorFocus: string;
  ambassadorPaymentMethod: string;
  ambassadorHasUploadedDocument: boolean;
  ambassadorGreenlightDate?: Date | null;
  // Computed server-side: true when the user's school — or, for an
  // ORGANIZATION host, the cohort they belong to — hosts a webinar. Such
  // campuses are covered by the webinar launch motion, so the "Be an
  // Ambassador" entry point is hidden for them.
  hasWebinarHost?: boolean;
  schoolId?: string | null;
  profilePictureUrl?: string;
  cohort?: string | null;
  marketingOfferKey?: string | null;
  attributionSource?: string | null;
  attributionMedium?: string | null;
  attributionCampaign?: string | null;
  attributionTerm?: string | null;
  attributionContent?: string | null;
  attributionCapturedAt?: Date | null;
  grandfathered?: boolean;
  studyReminderSendAt?: string;
  doctordleReminderEmailsEnabled?: boolean;
  blockSize?: number;
  homeLayout?: WIDGET_KEYS[];
  preferredLearningObjectiveLocation: string;
  verifiedEmails?: string[];
  tryAgainMode?: boolean;
  autoSubmitMode?: boolean;
  showTutorModeCallouts?: boolean;
  /** ORA-1091: the "How to approach this question" walkthrough after a reveal. */
  showQuestionCoach?: boolean;
  showVariantRetryPrompt?: boolean;
  /** Open on the Dashboard rather than the AI desktop (desktop browsers). */
  defaultToDashboard?: boolean;
  year?: string;
  yearUpdatedAt?: Date | null;
  yearStartMonth?: string;
  flashcardRightHandMode?: boolean;
  flashcardKeybinds?: FlashcardKeybinds;
  preferredVideoPlayback: number;
  videoQuestionsReviewMode?: boolean;
  lastSessionFeedbackDate?: SessionFeedbackTracking | null;
  hasDismissedAnkiSync: boolean;
  preferredFlashcardExplanation: ExplanationType;
  preferredLectureMappingRelevance: MappingRelevance;
  showSensitiveImages: boolean;
  studyDays?: number[];
  qbankShowStrikeoutIcon?: boolean;
  qbankStartConfig?: QbankStartConfig;
  /** Optional while older cached profiles and admin-list rows are upgraded. */
  instance?: ResolvedInstance;
  instanceOverrideId?: string | null;
}

export interface UpdateUserInfoRequest {
  name?: string | null;
  email?: string | null;
  timezone?: string;
  isOnboarded?: boolean;
  theme?: ThemeMode;
  highContrast?: boolean;
  demoMode?: boolean;
  screensaverPreference?: ScreensaverOption;
  preferredBlockTimeSetting?: BlockTimeSetting;
  responseOptions?: ResponseOption;
  showIntervals?: boolean;
  desiredRetention?: number;
  showAISidebar?: boolean;
  hapticsEnabled?: boolean;
  chatBlockThreadDefault?: boolean;
  onboardingTips?: { [key: string]: boolean };
  preferredBlockType?: string;
  qbankTheme?: 'standard' | 'ora';
  hasIOSApp?: boolean;
  hasAndroidApp?: boolean;
  flashcardTextSize?: string;
  vignetteTextSize?: string;
  vignetteMobileTextSize?: string;
  studyReminders?: string;
  subscribedToEmails?: boolean;
  emailPreferences?: Partial<NotificationPreferences>;
  ambassador?: boolean;
  ambassadorStartDate?: Date | null;
  buyIntent?: SubscriptionPlan | null;
  profilePictureUrl?: string;
  lastFeedbackTrigger?: FeedbackTrigger | null;
  studyReminderSendAt?: string;
  doctordleReminderEmailsEnabled?: boolean;
  blockSize?: number;
  homeLayout?: WIDGET_KEYS[];
  preferredLearningObjectiveLocation?: string;
  isAmbassadorOnboarded?: boolean;
  ambassadorAdvisoryBoard?: string;
  ambassadorOnWebsite?: string;
  ambassadorOnSlack?: string;
  ambassadorFocus?: string;
  ambassadorPaymentMethod?: string;
  ambassadorHasUploadedDocument?: boolean;
  ambassadorGreenlightDate?: Date | null;
  tryAgainMode?: boolean;
  shareGameScores?: boolean;
  autoSubmitMode?: boolean;
  showTutorModeCallouts?: boolean;
  /** ORA-1091: the "How to approach this question" walkthrough after a reveal. */
  showQuestionCoach?: boolean;
  showVariantRetryPrompt?: boolean;
  /** Open on the Dashboard rather than the AI desktop (desktop browsers). */
  defaultToDashboard?: boolean;
  /** Hover delay before a term's definition pop-up opens (web only). */
  explainerHoverDelay?: ExplainerHoverDelay;
  year?: string;
  yearUpdatedAt?: Date | null;
  yearStartMonth?: string | null;
  allowedBetaUser?: boolean;
  flashcardRightHandMode?: boolean;
  flashcardKeybinds?: FlashcardKeybinds;
  preferredVideoPlayback?: number;
  videoQuestionsReviewMode?: boolean;
  lastSessionFeedbackDate?: SessionFeedbackTracking | null;
  hasDismissedAnkiSync?: boolean;
  preferredFlashcardExplanation?: ExplanationType;
  preferredLectureMappingRelevance?: MappingRelevance;
  showSensitiveImages?: boolean;
  studyDays?: number[];
  clearTodaySession?: boolean;
  qbankShowStrikeoutIcon?: boolean;
  qbankStartConfig?: QbankStartConfig;
}
